/**
 * Supporting evidence chapter: Separation Health Assessment (SHA) flow for BDD
 * claims. Shown only when the claim qualifies for Benefits Delivery at
 * Discharge (isBDD). The upload page uses the v3 file uploader
 * (`va-file-input-multiple`) — all-claims-v2 always uses v3, with no feature
 * toggle fork.
 *
 * Flow:
 *   1. Question page - "Do you want to upload your SHA now?" (CustomPage)
 *   2. Upload page   - v3 multiple file upload (only when the answer is Yes)
 */
import React from 'react';
import { withUrlPrefix } from 'platform/forms-system/src/js/patterns/array-builder/helpers';
import { isBDD } from '../../utils/bdd';
import { SeparationHealthAssessment } from '../../components/SeparationHealthAssessment';
import {
  uiSchema as separationHealthAssessmentUiSchema,
  schema as separationHealthAssessmentSchema,
} from './separationHealthAssessment';
import {
  uiSchema as separationHealthAssessmentUploadUiSchema,
  schema as separationHealthAssessmentUploadSchema,
} from './separationHealthAssessmentUpload';
import { prepareFilesAccordion } from '../../content/fileUpload';

/** Question page path ("Do you want to upload your SHA now?"). */
export const SHA_QUESTION_PATH =
  'supporting-evidence/separation-health-assessment';

/** Upload page path — also the edit target for the summary section. */
export const SHA_UPLOAD_PATH =
  'supporting-evidence/separation-health-assessment-upload';

/** Form data key holding the uploaded SHA files. */
export const SHA_UPLOADS_ARRAY_PATH = 'separationHealthAssessmentUploads';

/**
 * Minimal, arrayBuilder-shaped options so the evidence summary can reuse
 * `ArrayBuilderCards` for the flat `va-file-input-multiple` SHA uploads. The
 * SHA field is not an arrayBuilder loop, so there's no intro/summary page and
 * edit routes to the single upload page.
 */
export const shaUploadSummaryOptions = {
  arrayPath: SHA_UPLOADS_ARRAY_PATH,
  nounSingular: 'Separation Health Assessment document',
  nounPlural: 'Separation Health Assessment documents',
  required: () => false,
  isItemIncomplete: item => !item?.confirmationCode,
  text: {
    getItemName: item =>
      item?.name || item?.fileName || 'Separation Health Assessment',
    deleteTitle: 'Delete file?',
    deleteDescription: ({ itemData, getItemName, index, formData }) => (
      <>
        <p className="vads-u-margin-top--0">We’ll delete this file:</p>
        <p>
          <strong>{getItemName(itemData, index, formData)}</strong>
        </p>
      </>
    ),
    deleteYes: 'Delete file',
    deleteNo: 'Keep file',
  },
};

const EVIDENCE_SUMMARY_PATH = 'supporting-evidence/summary';

/** In edit mode (`?edit=true` from the evidence summary), return there instead of following the normal flow. */
const returnToSummaryWhenEditing =
  defaultNavKey =>
  ({ urlParams, goPath, urlPrefix, ...navArgs }) =>
    urlParams?.edit
      ? goPath(withUrlPrefix(EVIDENCE_SUMMARY_PATH, urlPrefix))
      : navArgs[defaultNavKey]();

/** True when the BDD claimant chose to upload their SHA now. */
export const isUploadingSha = formData =>
  isBDD(formData) && formData['view:hasSeparationHealthAssessment'] === true;

export const separationHealthAssessmentPages = {
  separationHealthAssessment: {
    title: 'Separation Health Assessment that supports your disability claim',
    path: SHA_QUESTION_PATH,
    depends: formData => isBDD(formData),
    CustomPage: SeparationHealthAssessment,
    CustomPageReview: null,
    uiSchema: separationHealthAssessmentUiSchema,
    schema: separationHealthAssessmentSchema,
  },
  separationHealthAssessmentUpload: {
    title: 'Separation Health Assessment',
    path: SHA_UPLOAD_PATH,
    depends: formData => isUploadingSha(formData),
    uiSchema: separationHealthAssessmentUploadUiSchema,
    schema: separationHealthAssessmentUploadSchema,
    ContentBeforeButtons: () => prepareFilesAccordion,
    onNavForward: returnToSummaryWhenEditing('goNextPath'),
    onNavBack: returnToSummaryWhenEditing('goPreviousPath'),
  },
};
